import {
  useCadModelObjectLimitedBoundingBox,
  useObjectBoundingBox,
} from "@/hooks/use-object-bounding-box";
import { Mode, ModeTransitionProps } from "@/modes/mode";
import { useCached3DObject } from "@/object-cache";
import { selectActiveCadModel } from "@/store/cad/cad-slice";
import { changeMode } from "@/store/mode-slice";
import {
  selectCloudAndCadAlignReference,
  selectCloudForCadAlignment,
} from "@/store/modes/cloud-cad-alignment-mode-selectors";
import {
  AlignmentReference,
  CloudCadAlignmentStep,
  resetCloudCadAlignment,
  setCloudElevationForCloudCadAlignment,
  setModelElevationForCloudCadAlignment,
  setStepForCloudCadAlignment,
} from "@/store/modes/cloud-cad-alignment-mode-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { assert } from "@faro-lotv/foundation";
import { isIElementGenericPointCloudStream } from "@faro-lotv/ielement-types";
import { selectIElement } from "@faro-lotv/project-source";
import { useEffect } from "react";
import { Vector3 } from "three";
import { CloudCadAlignmentModeOverlay } from "./cloud-cad-alignment-mode-overlay";
import { CloudCadAlignmentModeScene } from "./cloud-cad-alignment-mode-scene";

// Mode for both cloud-cad and cad-to-cloud alignments
export const cloudCadAlignmentMode: Mode = {
  name: "cloudCadAlignment",
  ModeScene: CloudCadAlignmentModeScene,
  ModeOverlay: CloudCadAlignmentModeOverlay,
  ModeTransition: CloudCadAlignmentModeTransition,
  hasCustomCanvasStyle: true,
  exclusive: {
    title: (state) => {
      const alignmentReference = selectCloudAndCadAlignReference(state);
      return alignmentReference === AlignmentReference.bimModel
        ? "Align cloud to 3D model"
        : "Align 3D model to cloud";
    },
    onBack({ dispatch }) {
      dispatch(resetCloudCadAlignment());

      dispatch(changeMode("overview"));
      return Promise.resolve();
    },
  },
};

function CloudCadAlignmentModeTransition({
  onCompleted,
}: ModeTransitionProps): null {
  const activeCloudID = useAppSelector(selectCloudForCadAlignment);
  const activeCloud = useAppSelector(selectIElement(activeCloudID));
  const activeCad = useAppSelector(selectActiveCadModel);
  assert(
    activeCad && activeCloud && isIElementGenericPointCloudStream(activeCloud),
    "Cloud and CAd Alignment Mode requires cad and cloud",
  );

  const cadModel = useCached3DObject(
    activeCad,
    "CloudCadAlignmentModeTransition",
  );
  const cadBox = useCadModelObjectLimitedBoundingBox(
    cadModel,
    cadModel.iElement.id,
  );

  const pointCloud = useCached3DObject(
    activeCloud,
    "CloudCadAlignmentModeTransition",
  );
  const cloudBox = useObjectBoundingBox(pointCloud, activeCloud.id);
  assert(cloudBox, "Expected cloud has bounding box computed on loading");

  // Get the elevation value saved from the previous cloud-cad alignment
  // The value is same for both cad and the aligned cloud.
  const cloudElevation =
    activeCloud.metaDataMap?.cloudToCadElevation !== undefined &&
    typeof activeCloud.metaDataMap.cloudToCadElevation === "number"
      ? activeCloud.metaDataMap.cloudToCadElevation
      : undefined;

  // Get the elevation value saved from the previous cad-to-cloud alignment
  // The value is same for both cad and the aligned cloud.
  const cadElevation =
    activeCad.metaDataMap?.cadToCloudElevation !== undefined &&
    typeof activeCad.metaDataMap.cadToCloudElevation === "number"
      ? activeCad.metaDataMap.cadToCloudElevation
      : undefined;

  const reference = useAppSelector(selectCloudAndCadAlignReference);

  const elevation =
    reference === AlignmentReference.bimModel ? cloudElevation : cadElevation;

  const dispatch = useAppDispatch();

  useEffect(() => {
    // make sure to start with the first step (set elevations)
    dispatch(setStepForCloudCadAlignment(CloudCadAlignmentStep.setElevations));

    const cadCenter = cadBox.boundingBox
      ? cadBox.boundingBox.getCenter(new Vector3())
      : new Vector3();
    dispatch(setModelElevationForCloudCadAlignment(elevation ?? cadCenter.y));

    const cloudCenter = cloudBox.getCenter(new Vector3());
    dispatch(setCloudElevationForCloudCadAlignment(elevation ?? cloudCenter.y));

    onCompleted();
  }, [cadBox, cloudBox, dispatch, elevation, onCompleted]);

  return null;
}
